// src/pages/Clients.jsx
// Amministrazione (solo nella nostra copia, solo admin): le copie di
// RaceSense Live dei clienti e le sezioni che hanno acquistato. Spegnendo una
// sezione il cliente non la vede più e il suo server rifiuta di avviarla;
// la modifica vale subito, senza riavvii. Per ogni cliente c'è anche il backup
// settimanale degli storici (allenamenti e gare concluse): ultimo, prossimo,
// archivi salvati da scaricare.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../components/icons';
import { useSession } from '../session';
import './Clients.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

const SECTIONS = [
  { key: 'race', label: 'Gara', desc: 'Regia gara, classifica live, gare passate, landing gara', icon: Icon.Play },
  { key: 'training', label: 'Allenamento', desc: 'Sessioni di test, telemetria, allenamenti passati', icon: Icon.Activity },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso)); } catch { return '—'; }
};

// Orari dei backup: sempre in ora italiana, come la pianificazione
const fmtWhen = (iso) => {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('it-IT', { timeZone: 'Europe/Rome', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso)); } catch { return '—'; }
};
const fmtDay = (iso) => {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('it-IT', { timeZone: 'Europe/Rome', day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso)); } catch { return '—'; }
};
const fmtBytes = (b) => {
  if (!Number.isFinite(b)) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = b;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toLocaleString('it-IT', { maximumFractionDigits: i > 0 && v < 100 ? 1 : 0 })} ${u[i]}`;
};
const fmtRelative = (iso, now) => {
  const t = Date.parse(iso || '');
  if (!t) return '';
  const d = t - now;
  const abs = Math.abs(d);
  if (d >= 0 && abs < 3 * 60000) return 'a breve';
  const min = Math.round(abs / 60000);
  const h = Math.round(abs / 3600000);
  const days = Math.round(abs / 86400000);
  const s = min < 60 ? `${Math.max(1, min)} min` : h < 36 ? `${h} ${h === 1 ? 'ora' : 'ore'}` : `${days} giorni`;
  return d >= 0 ? `tra ${s}` : `${s} fa`;
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const sessionsLabel = (t, r) => `${plural(t || 0, 'allenamento', 'allenamenti')} · ${plural(r || 0, 'gara', 'gare')}`;

/** Sessioni contenute in un archivio (caricate quando si apre). */
function BackupSessions({ entry }) {
  if (!entry || entry.loading) return <div className="cl-sess__loading"><span className="rs-spinner" />Caricamento dell'elenco…</div>;
  if (entry.error) return <div className="rs-alert rs-alert--danger"><Icon.Alert /><span>{entry.error}</span></div>;
  const d = entry.data;
  const rows = [
    ...(d.trainingList || []).map(t => ({
      key: `t-${t.id}`, race: false, when: t.startTime, title: t.circuit || t.id,
      sub: (t.drivers || []).join(' · '), bytes: t.bytes,
    })),
    ...(d.raceList || []).map(r => ({
      key: `r-${r.folder}`, race: true, when: r.startTime, title: [r.name, r.circuit].filter(Boolean).join(' · ') || r.folder,
      sub: r.drivers != null ? plural(r.drivers, 'pilota', 'piloti') : '', bytes: r.bytes,
    })),
  ].sort((a, b) => String(b.when || '').localeCompare(String(a.when || '')));
  return (
    <div className="cl-sess">
      {rows.length ? (
        <ul className="cl-sess__list">
          {rows.map(r => (
            <li key={r.key}>
              <span className={`cl-sess__kind${r.race ? ' is-race' : ''}`}>{r.race ? 'Gara' : 'Allenamento'}</span>
              <span className="cl-sess__when">{fmtWhen(r.when)}</span>
              <span className="cl-sess__text"><strong>{r.title}</strong>{r.sub ? <small>{r.sub}</small> : null}</span>
              <span className="cl-sess__size">{fmtBytes(r.bytes)}</span>
            </li>
          ))}
        </ul>
      ) : <p className="cl-sess__empty">Nessuna sessione in questo archivio.</p>}
      {d.sha256 && <p className="cl-sess__hash" title={d.sha256}>Impronta SHA-256 dell'archivio: <code>{d.sha256.slice(0, 16)}…</code></p>}
    </div>
  );
}

/** Backup degli storici di un cliente: stato, azioni e archivi salvati. */
function BackupPanel({ info, schedule, keep, running, now, onRun }) {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(null);       // archivio con l'elenco aperto
  const [details, setDetails] = useState({});     // nome → { loading | data | error }
  const [msg, setMsg] = useState(null);           // { kind, text }
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const wasRunning = useRef(false);

  // fine di un backup seguito da questa pagina: esito in chiaro
  useEffect(() => {
    if (wasRunning.current && !info.running) {
      const failedNow = !!info.lastError && (!info.lastOkAt || Date.parse(info.lastErrorAt || '') > Date.parse(info.lastOkAt));
      if (failedNow) setMsg({ kind: 'danger', text: `Backup non riuscito: ${info.lastError}` });
      else if (info.lastResult === 'created') setMsg({ kind: 'success', text: 'Backup completato: nuovo archivio salvato e verificato.' });
      else if (info.lastResult === 'unchanged') setMsg({ kind: 'success', text: 'Backup completato: nessuna sessione nuova, l\'ultimo archivio è già aggiornato.' });
      else if (info.lastResult === 'empty') setMsg({ kind: 'info', text: 'Nessuna sessione conclusa da salvare per questo cliente.' });
    }
    wasRunning.current = !!info.running;
  }, [info.running, info.lastError, info.lastErrorAt, info.lastOkAt, info.lastResult]);

  const latest = info.archives?.[0] || null;
  const failed = !!info.lastError && (!info.lastOkAt || Date.parse(info.lastErrorAt || '') > Date.parse(info.lastOkAt));

  const runNow = async () => {
    setStarting(true);
    setMsg(null);
    // un backup senza novità finisce prima del primo aggiornamento: l'esito va mostrato comunque
    wasRunning.current = true;
    try {
      await onRun(info.id);
    } catch (e) {
      wasRunning.current = false;
      setMsg({ kind: 'danger', text: e.message });
    } finally {
      setStarting(false);
    }
  };

  const download = async (a) => {
    setDownloading(a.name);
    setMsg(null);
    try {
      const r = await fetch(`${API_BASE}/api/clients/${encodeURIComponent(info.id)}/backups/${encodeURIComponent(a.name)}/link`, { method: 'POST' });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.url) throw new Error(j?.error || 'Download non disponibile');
      // link usa e getta: il browser scarica da solo, con la sua barra di avanzamento
      const link = document.createElement('a');
      link.href = `${API_BASE}${j.url}`;
      link.download = a.file;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setMsg({ kind: 'success', text: `Download avviato: ${a.file} (${fmtBytes(a.sizeBytes)}). L'avanzamento è nei download del browser.` });
    } catch (e) {
      setMsg({ kind: 'danger', text: e.message });
    } finally {
      setDownloading(null);
    }
  };

  const toggleSessions = async (a) => {
    if (shown === a.name) { setShown(null); return; }
    setShown(a.name);
    if (details[a.name]?.data) return;
    setDetails(d => ({ ...d, [a.name]: { loading: true } }));
    try {
      const r = await fetch(`${API_BASE}/api/clients/${encodeURIComponent(info.id)}/backups/${encodeURIComponent(a.name)}`);
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error(j?.error || 'Elenco delle sessioni non disponibile');
      setDetails(d => ({ ...d, [a.name]: { data: j } }));
    } catch (e) {
      setDetails(d => ({ ...d, [a.name]: { error: e.message } }));
    }
  };

  if (!info.hasData) {
    return (
      <div className="cl-backup is-off">
        <div className="cl-backup__head">
          <span className="cl-backup__icon"><Icon.Archive size={16} /></span>
          <div className="cl-backup__title">
            <strong>Backup storico</strong>
            <small>La copia di questo cliente non è su questo server: non c'è niente da salvare.</small>
          </div>
        </div>
      </div>
    );
  }

  let lastNote = '';
  if (info.running) lastNote = 'Backup in corso…';
  else if (failed) lastNote = info.lastOkAt ? 'Ultimo backup riuscito · il tentativo successivo non è andato' : 'Il primo tentativo non è riuscito';
  else if (info.lastResult === 'created') lastNote = 'Nuovo archivio creato e verificato';
  else if (info.lastResult === 'unchanged') lastNote = 'Nessuna sessione nuova: archivio confermato';
  else if (info.lastResult === 'empty') lastNote = 'Nessuna sessione conclusa da salvare';
  else lastNote = `Il primo backup parte ${fmtRelative(info.nextAt, now) || 'a breve'}`;
  if (!info.running && info.skipped > 0) lastNote += ` · ${plural(info.skipped, 'gara appena conclusa entra', 'gare appena concluse entrano')} nel prossimo`;

  const busy = info.running || starting;
  return (
    <div className="cl-backup">
      <div className="cl-backup__head">
        <span className="cl-backup__icon"><Icon.Archive size={16} /></span>
        <div className="cl-backup__title">
          <strong>Backup storico</strong>
          <small>Allenamenti e gare concluse · {schedule?.label?.toLowerCase() || 'settimanale'} (ora italiana)</small>
        </div>
        <div className="cl-backup__actions">
          <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost" onClick={runNow} disabled={busy || !!running}
            title={running && !info.running ? `Backup di ${running.label} in corso: attendi che finisca` : 'Salva adesso le sessioni concluse'}>
            {busy ? <><span className="rs-spinner cl-spin" />In corso…</> : <><Icon.Zap size={14} />Esegui ora</>}
          </button>
          <button type="button" className="rs-btn rs-btn--sm rs-btn--primary" onClick={() => latest && download(latest)} disabled={!latest || downloading === latest?.name}
            title={latest ? `Scarica ${latest.file}` : 'Nessun archivio salvato'}>
            <Icon.Download size={14} />Scarica ultimo
          </button>
        </div>
      </div>

      <div className="cl-backup__stats">
        <div className={`cl-stat${failed && !info.running ? ' is-bad' : info.lastOkAt ? ' is-ok' : ''}`}>
          <span className="cl-stat__label">Ultimo backup</span>
          <strong>{info.lastOkAt ? fmtWhen(info.lastOkAt) : 'Non ancora eseguito'}</strong>
          <small>{lastNote}</small>
        </div>
        <div className="cl-stat">
          <span className="cl-stat__label">Prossimo backup</span>
          <strong>{info.running ? 'In corso adesso' : fmtWhen(info.nextAt)}</strong>
          <small>{info.running ? (running?.phase || 'Avvio') : fmtRelative(info.nextAt, now)}</small>
        </div>
        <div className="cl-stat">
          <span className="cl-stat__label">Ultimo archivio</span>
          <strong>{latest ? sessionsLabel(latest.trainings, latest.races) : '—'}</strong>
          <small>{latest ? `${fmtBytes(latest.sizeBytes)} · creato il ${fmtDay(latest.createdAt)}` : 'Nessun archivio salvato'}</small>
        </div>
      </div>

      {info.running && (
        <div className="cl-run" role="status">
          <span className="cl-run__text"><span className="rs-spinner cl-spin" />{running?.phase || 'Avvio'}…</span>
          <span className="cl-run__bar"><span /></span>
        </div>
      )}
      {failed && !info.running && (
        <div className="rs-alert rs-alert--danger">
          <Icon.Alert />
          <span>
            <strong>Backup non riuscito</strong> ({fmtWhen(info.lastErrorAt)}): {info.lastError}.{' '}
            {info.overdue
              ? `Nuovo tentativo automatico ${fmtRelative(info.nextAt, now) || 'a breve'}.`
              : `Resta valido l'ultimo backup riuscito (${fmtWhen(info.lastOkAt)}); il prossimo automatico è ${fmtWhen(info.nextAt)}.`}
          </span>
        </div>
      )}
      {msg && !(msg.kind === 'danger' && failed && !info.running && msg.text.includes(info.lastError)) && (
        <div className={`rs-alert rs-alert--${msg.kind}`}>{msg.kind === 'danger' ? <Icon.Alert /> : msg.kind === 'info' ? <Icon.Info /> : <Icon.Check />}<span>{msg.text}</span></div>
      )}

      {info.archives?.length > 0 && (
        <div className="cl-arch">
          <button type="button" className="cl-arch__toggle" aria-expanded={open} onClick={() => setOpen(o => !o)}>
            <Icon.ChevronDown size={15} className={`cl-arch__chev${open ? ' is-open' : ''}`} />
            Archivi salvati <b>{info.archives.length}</b>
            <span className="cl-arch__hint">restano gli ultimi {keep}</span>
          </button>
          {open && (
            <div className="rs-table-wrap cl-arch__wrap">
              <table className="rs-table cl-arch__table">
                <thead>
                  <tr>
                    <th>Creato</th>
                    <th>Contenuto</th>
                    <th className="num">Dimensione</th>
                    <th className="cl-col-origin">Origine</th>
                    <th aria-label="Azioni" />
                  </tr>
                </thead>
                <tbody>
                  {info.archives.map((a, i) => (
                    <React.Fragment key={a.name}>
                      <tr className={shown === a.name ? 'is-open' : ''}>
                        <td className="cl-arch__when">
                          <span>{fmtWhen(a.createdAt)}{i === 0 && <span className="rs-badge rs-badge--lime cl-arch__badge">Ultimo</span>}</span>
                          {a.confirmedAt && <small>confermato {fmtWhen(a.confirmedAt)}</small>}
                        </td>
                        <td>{sessionsLabel(a.trainings, a.races)}</td>
                        <td className="num cl-mono">{fmtBytes(a.sizeBytes)}</td>
                        <td className="cl-col-origin">{a.trigger === 'manual' ? 'Manuale' : 'Automatico'}</td>
                        <td className="cl-arch__btns">
                          <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost" onClick={() => toggleSessions(a)} aria-expanded={shown === a.name}>
                            <Icon.Eye size={14} />Sessioni
                          </button>
                          <button type="button" className="rs-btn rs-btn--sm" onClick={() => download(a)} disabled={downloading === a.name} title={`Scarica ${a.file}`}>
                            <Icon.Download size={14} />Scarica
                          </button>
                        </td>
                      </tr>
                      {shown === a.name && (
                        <tr className="cl-arch__detail">
                          <td colSpan={5}><BackupSessions entry={details[a.name]} /></td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Switch({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`cl-switch${checked ? ' is-on' : ''}`}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className="cl-switch__knob" />
    </button>
  );
}

export default function Clients() {
  const session = useSession();
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState({});   // `${id}:${key}` → true
  const [rowError, setRowError] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/clients`);
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error(j?.error || 'Impossibile leggere i clienti');
      setItems(Array.isArray(j) ? j : []);
      setError('');
    } catch (e) {
      setError(e.message);
      setItems(prev => prev || []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Backup degli storici
  const [backups, setBackups] = useState(null);
  const [backupError, setBackupError] = useState('');
  const [now, setNow] = useState(Date.now());
  const loadBackups = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/clients/backups`);
      const j = await r.json().catch(() => null);
      if (!r.ok || !j) throw new Error(j?.error || 'Stato dei backup non disponibile');
      setBackups(j);
      setBackupError('');
    } catch (e) {
      setBackupError(e.message);
    }
    setNow(Date.now());
  }, []);
  useEffect(() => { loadBackups(); }, [loadBackups]);
  // con un backup in corso lo stato si segue da vicino, altrimenti ogni minuto
  const backupRunning = !!backups?.running;
  useEffect(() => {
    const t = setInterval(loadBackups, backupRunning ? 2000 : 60000);
    return () => clearInterval(t);
  }, [backupRunning, loadBackups]);
  const runBackup = useCallback(async (id) => {
    const r = await fetch(`${API_BASE}/api/clients/${encodeURIComponent(id)}/backups/run`, { method: 'POST' });
    const j = await r.json().catch(() => null);
    if (!r.ok) throw new Error(j?.error || 'Backup non avviato');
    await loadBackups();
  }, [loadBackups]);
  const backupOf = (id) => backups?.clients?.find(b => b.id === id) || null;

  const toggle = async (client, key, value) => {
    const k = `${client.id}:${key}`;
    setSaving(s => ({ ...s, [k]: true }));
    setRowError(e => ({ ...e, [client.id]: '' }));
    // aggiornamento immediato a video, annullato se il server rifiuta
    setItems(list => list.map(c => (c.id === client.id ? { ...c, features: { ...c.features, [key]: value } } : c)));
    try {
      const r = await fetch(`${API_BASE}/api/clients/${encodeURIComponent(client.id)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ features: { [key]: value } }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error(j?.error || 'Salvataggio non riuscito');
      setItems(list => list.map(c => (c.id === client.id ? { ...c, ...j } : c)));
    } catch (e) {
      setItems(list => list.map(c => (c.id === client.id ? { ...c, features: { ...c.features, [key]: !value } } : c)));
      setRowError(er => ({ ...er, [client.id]: e.message }));
    } finally {
      setSaving(s => { const n = { ...s }; delete n[k]; return n; });
    }
  };

  if (session && !session.canManageClients) {
    return (
      <div className="rs-page">
        <div className="rs-empty"><Icon.Lock /><strong>Pagina riservata</strong><p>La gestione dei clienti è disponibile solo all'amministratore di RaceSense.</p></div>
      </div>
    );
  }

  return (
    <div className="rs-page cl-page">
      <header className="rs-page-header">
        <div className="rs-page-header__text">
          <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot" />Amministrazione</span>
          <h1 className="rs-page-header__title">Clienti</h1>
          <p className="rs-page-header__subtitle">Copie di RaceSense Live dei clienti, sezioni attive e backup degli storici. Le modifiche alle sezioni valgono subito: il cliente vede solo quelle accese.</p>
        </div>
        <div className="rs-page-header__actions">
          <button type="button" className="rs-btn rs-btn--sm" onClick={() => { load(); loadBackups(); }}>Aggiorna stato</button>
        </div>
      </header>

      {error && <div className="rs-alert rs-alert--danger"><Icon.Alert /><span>{error}</span></div>}

      {backups && (
        <div className="cl-bkinfo">
          <span className="cl-bkinfo__icon"><Icon.Archive size={16} /></span>
          <span className="cl-bkinfo__text">
            <strong>Backup automatico degli storici</strong>
            <small>{backups.schedule?.label} (ora italiana) · solo allenamenti e gare concluse dei clienti, non i dati di RaceSense · restano gli ultimi {backups.keep} archivi per cliente</small>
          </span>
          {backups.disk && (
            <span className="cl-bkinfo__disk" title="Spazio libero sul disco del server">
              <small>Spazio libero</small>
              <b>{fmtBytes(backups.disk.freeBytes)}</b>
            </span>
          )}
        </div>
      )}
      {backupError && <div className="rs-alert rs-alert--warning"><Icon.Alert /><span>Backup: {backupError}</span></div>}

      {items == null ? (
        <div className="rs-empty"><div className="rs-spinner" /><strong>Caricamento…</strong></div>
      ) : items.length === 0 ? (
        <div className="rs-empty">
          <Icon.Users />
          <strong>Nessun cliente registrato</strong>
          <p>Ogni cliente ha una copia dedicata della piattaforma, creata sul server con il suo dominio e i suoi accessi.</p>
        </div>
      ) : (
        <div className="cl-list">
          {items.map(c => {
            const allOff = SECTIONS.every(s => c.features?.[s.key] === false);
            return (
              <section key={c.id} className="cl-card">
                <div className="cl-card__id">
                  <div className="cl-card__name">
                    <span className={`cl-dot ${c.online ? 'is-on' : c.online === false ? 'is-off' : ''}`} title={c.online ? 'Copia raggiungibile' : c.online === false ? 'Copia non raggiungibile' : 'Stato sconosciuto'} />
                    <h2>{c.label}</h2>
                  </div>
                  {c.url && <a className="cl-card__url" href={c.url} target="_blank" rel="noopener noreferrer">{c.url.replace(/^https?:\/\//, '')}</a>}
                  <dl className="cl-card__meta">
                    <div><dt>Stato</dt><dd>{c.online ? 'Online' : c.online === false ? 'Non raggiungibile' : '—'}</dd></div>
                    <div><dt>Ultima modifica</dt><dd>{fmtDate(c.updatedAt)}</dd></div>
                  </dl>
                </div>

                <div className="cl-card__sections">
                  {SECTIONS.map(s => {
                    const on = c.features?.[s.key] !== false;
                    const Ic = s.icon;
                    return (
                      <div key={s.key} className={`cl-section${on ? ' is-on' : ''}`}>
                        <span className="cl-section__icon"><Ic size={16} /></span>
                        <span className="cl-section__text">
                          <strong>{s.label}</strong>
                          <small>{s.desc}</small>
                        </span>
                        <span className="cl-section__state">{on ? 'Attiva' : 'Spenta'}</span>
                        <Switch checked={on} disabled={!!saving[`${c.id}:${s.key}`]} onChange={(v) => toggle(c, s.key, v)} label={`${s.label} per ${c.label}`} />
                      </div>
                    );
                  })}
                  {allOff && <div className="cl-warn"><Icon.Alert />Con tutte le sezioni spente il cliente vede solo anagrafiche e circuiti.</div>}
                  {rowError[c.id] && <div className="rs-alert rs-alert--danger"><Icon.Alert /><span>{rowError[c.id]}</span></div>}
                </div>

                <div className="cl-card__actions">
                  {c.url && (
                    <a className="rs-btn rs-btn--sm" href={c.url} target="_blank" rel="noopener noreferrer"><Icon.Share size={14} />Apri</a>
                  )}
                </div>

                <div className="cl-card__backup">
                  {backupOf(c.id) ? (
                    <BackupPanel info={backupOf(c.id)} schedule={backups.schedule} keep={backups.keep} running={backups.running} now={now} onRun={runBackup} />
                  ) : (
                    <div className="cl-backup is-off">
                      <div className="cl-backup__head">
                        <span className="cl-backup__icon"><Icon.Archive size={16} /></span>
                        <div className="cl-backup__title">
                          <strong>Backup storico</strong>
                          <small>{backupError ? 'Stato non disponibile' : 'Caricamento…'}</small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <p className="cl-foot">Clienti, nomi, indirizzi e porte GPS si gestiscono solo dal pannello OTA (Organizzazioni); un cliente nuovo ha poi bisogno della sua copia sul server (dominio e accessi). Da qui si gestiscono le sezioni e si scaricano i backup.</p>
    </div>
  );
}
